import { useCallback, useMemo } from 'react'
import useBundleTradeStore from './bundleTradeStore'
import { SubmissionStatus, type ReadyMadeTotals, type SecurityOrderItem } from './types'
import { usePlaceBuyOrder } from '@/trading/tradingQueries'
import { analytics } from '@/common/analytics/analytics'
import { ANALYTICS_EVENTS } from '@/common/analytics/events'
import * as Sentry from '@sentry/react'
import { RewardType } from '@/common/types'
import { useOrderSource } from '@/common/context/useOrderSource'
import useAccountStore from '@/account/accountStore'

export default function useBundleTrade() {
    const { orderSource } = useOrderSource()
    const activeAccountId = useAccountStore((s) => s.activeAccountId)
    const isSubmitting = useBundleTradeStore((s) => s.isSubmitting)
    const setIsSubmitting = useBundleTradeStore((s) => s.setIsSubmitting)

    const orderItems = useBundleTradeStore((s) => s.orderItems)
    const setOrderItems = useBundleTradeStore((s) => s.setOrderItems)
    const addOrderItem = useBundleTradeStore((s) => s.addOrderItem)
    const updateOrderItem = useBundleTradeStore((s) => s.updateOrderItem)
    const removeOrderItem = useBundleTradeStore((s) => s.removeOrderItem)
    const clearOrderItems = useBundleTradeStore((s) => s.clearOrderItems)
    const orderExpiry = useBundleTradeStore((s) => s.orderExpiry)
    const setOrderExpiry = useBundleTradeStore((s) => s.setOrderExpiry)
    const totalInvestmentAmount = useBundleTradeStore((s) => s.totalInvestmentAmount)
    const setTotalInvestmentAmount = useBundleTradeStore((s) => s.setTotalInvestmentAmount)
    const bundleRequestId = useBundleTradeStore((s) => s.bundleRequestId)
    const setRequestId = useBundleTradeStore((s) => s.setBundleRequestId)
    const orderType = useBundleTradeStore((s) => s.orderType)
    const setOrderType = useBundleTradeStore((s) => s.setOrderType)
    const resetState = useBundleTradeStore((s) => s.resetState)
    const name = useBundleTradeStore((s) => s.name)
    const bundleId = useBundleTradeStore((s) => s.bundleId)
    const initiateBundleTrade = useBundleTradeStore((s) => s.initiateBundleTrade)
    const hasFundsError = useBundleTradeStore((s) => s.hasFundsError)
    const setHasFundsError = useBundleTradeStore((s) => s.setHasFundsError)
    const promotions = useBundleTradeStore((s) => s.promotions)
    const setPromotions = useBundleTradeStore((s) => s.setPromotions)

    const placeBuyOrder = usePlaceBuyOrder()

    const totals: ReadyMadeTotals = useMemo(() => {
        const totalAmount = orderItems.reduce((acc, l) => acc + (l.amount || 0), 0)
        const brokerage = orderItems.filter((l) => l.units > 0).length * 9.9
        return { totalAmount, brokerage, total: totalAmount + brokerage }
    }, [orderItems])

    const submitSequential = useCallback(async () => {
        const success: SecurityOrderItem[] = []
        const failed: { line: SecurityOrderItem; reason?: string }[] = []

        if (!bundleRequestId || !orderType || !activeAccountId) return
        setIsSubmitting(true)

        analytics.trackWithUtm(ANALYTICS_EVENTS.TRADING.BUNDLE_TRADE_SUBMITTED, {
            request_id: bundleRequestId,
            security_codes: orderItems.map((item) => item.securityCode).join(','),
            type: orderType,
            validity: orderExpiry,
            estimated_amount: totals.totalAmount,
            bundle_id: bundleId,
            bundle_request_id: bundleRequestId,
            promotion_applied: promotions && promotions.length > 0,
            order_from: orderSource.orderFrom,
            order_entry_point: orderSource.orderEntryPoint,
            accountId: activeAccountId,
        })

        const promoPool = (promotions || []).map((p) => ({ ...p }))

        for (const line of orderItems) {
            if (line.units === 0) continue
            try {
                let appliedPromotionId: string | undefined
                const promoIndex = promoPool.findIndex(
                    (p) => p.promotionType === RewardType.FREE_TRADE_REBATE && (p.quantityRemaining ?? 0) > 0,
                )
                if (promoIndex !== -1) {
                    appliedPromotionId = promoPool[promoIndex].promotionId
                    promoPool[promoIndex].quantityRemaining = (promoPool[promoIndex].quantityRemaining ?? 0) - 1
                }

                const payload: any = {
                    requestId: line.requestId,
                    orderType: orderType,
                    requestedPrice: orderType === 'LIMIT' ? line.relevantPrice : undefined,
                    units: line.units,
                    validity: orderExpiry,
                    securityCode: line.securityCode,
                    bundleId: bundleId,
                    bundleRequestId: bundleRequestId,
                    accountId: activeAccountId,
                    ...(appliedPromotionId ? { promotionId: appliedPromotionId } : {}),
                }

                analytics.trackWithUtm(ANALYTICS_EVENTS.TRADING.BUY_ORDER_SUBMITTED, {
                    request_id: payload.requestId,
                    security_code: payload.securityCode,
                    type: payload.orderType,
                    requested_price: payload.requestedPrice,
                    number_of_shares: payload.units,
                    validity: payload.validity,
                    bundle_id: payload.bundleId,
                    bundle_request_id: payload.bundleRequestId,
                    order_from: orderSource.orderFrom,
                    order_entry_point: orderSource.orderEntryPoint,
                    accountId: activeAccountId,
                    ...(appliedPromotionId
                        ? { promotionId: appliedPromotionId, promotion: RewardType.FREE_TRADE_REBATE }
                        : {}),
                })

                await placeBuyOrder.mutateAsync(payload)

                updateOrderItem(line.securityCode, { submissionStatus: SubmissionStatus.SUCCESS })
                success.push(line)
            } catch (e: any) {
                try {
                    Sentry.captureException(e, {
                        level: 'error',
                        extra: {
                            url: e?.config?.url,
                            method: e?.config?.method,
                            status: e?.response?.status,
                            responseData: e?.response?.data,
                            requestId: e?.requestId,
                            traceId: e?.traceId,
                        },
                    })
                } catch {
                    // swallow Sentry errors
                }
                analytics.trackWithDefaults(ANALYTICS_EVENTS.TRADING.BUY_ORDER_FAILED, {
                    request_id: line.requestId,
                    security_code: line.securityCode,
                    type: orderType,
                    requested_price: line.relevantPrice,
                    number_of_shares: line.units,
                    validity: orderExpiry,
                    bundle_id: bundleId,
                    bundle_request_id: bundleRequestId,
                    error_message: (e as Error)?.message,
                    accountId: activeAccountId,
                })
                updateOrderItem(line.securityCode, { submissionStatus: SubmissionStatus.FAILED })
                failed.push({ line, reason: (e as Error)?.message })
            }
        }

        setIsSubmitting(false)
        return { success, failed }
    }, [
        bundleRequestId,
        orderType,
        activeAccountId,
        setIsSubmitting,
        orderItems,
        orderExpiry,
        totals.totalAmount,
        bundleId,
        promotions,
        orderSource.orderFrom,
        orderSource.orderEntryPoint,
        placeBuyOrder,
        updateOrderItem,
    ])

    return {
        // Line management
        orderItems,
        setOrderItems,
        addOrderItem,
        updateOrderItem,
        removeOrderItem,
        clearOrderItems,

        setPromotions,
        promotions,

        // Global settings
        orderType,
        setOrderType,
        orderExpiry,
        setOrderExpiry,
        totalInvestmentAmount,
        setTotalInvestmentAmount,
        setRequestId,
        resetState,
        name,
        bundleId,
        initiateBundleTrade,

        // Computed
        totals,
        hasFundsError,
        setHasFundsError,

        // Actions
        submitSequential,
        isSubmitting,
    }
}
